import { Plan } from "../../../../types/Plan";
import {
  LearningLevel,
  LearningPlacementRouting,
} from "../../../../types/Questionnaire";

export const LEARNING_PLACEMENT_LEVELS: Array<{
  key: LearningLevel;
  label: string;
  description: string;
}> = [
  {
    key: "beginner",
    label: "Beginner",
    description: "Foundations and the safest starting point.",
  },
  {
    key: "intermediate",
    label: "Intermediate",
    description: "Applied work for members with working knowledge.",
  },
  {
    key: "advanced",
    label: "Advanced",
    description: "Later-stage material that assumes prior mastery.",
  },
];

export type LearningPlacementFormValues = {
  beginnerPlanId: string;
  intermediatePlanId: string;
  advancedPlanId: string;
};

const FIELD_BY_LEVEL: Record<
  LearningLevel,
  keyof LearningPlacementFormValues
> = {
  beginner: "beginnerPlanId",
  intermediate: "intermediatePlanId",
  advanced: "advancedPlanId",
};

export const buildLearningPlacementRouting = (
  values: LearningPlacementFormValues
): LearningPlacementRouting => {
  const routing: LearningPlacementRouting = {};

  for (const { key } of LEARNING_PLACEMENT_LEVELS) {
    const planId = String(values[FIELD_BY_LEVEL[key]] || "").trim();
    if (planId) routing[key] = planId;
  }

  return routing;
};

export const mergeQuestionnaireWithLearningPlacement = <
  T extends Record<string, any>,
>(
  source: T,
  editedFields: Record<string, any>,
  routing: LearningPlacementRouting
): T => {
  const {
    learningPlacementRouting: _previousLearningPlacementRouting,
    ...preservedFields
  } = source || ({} as T);

  return {
    ...preservedFields,
    ...editedFields,
    ...(Object.keys(routing).length
      ? { learningPlacementRouting: routing }
      : {}),
  } as T;
};

export const validateLearningPlacementSelection = ({
  values,
  plans,
  plansLoading,
  plansError,
}: {
  values: LearningPlacementFormValues;
  plans: Plan[];
  plansLoading?: boolean;
  plansError?: boolean;
}): string | null => {
  const routing = buildLearningPlacementRouting(values);
  const selectedIds = Object.values(routing);
  if (!selectedIds.length) return null;

  if (plansLoading) {
    return "Wait for the learning plans to finish loading before saving placement.";
  }
  if (plansError) {
    return "Learning plans could not be loaded. Placement was not saved.";
  }

  if (new Set(selectedIds).size !== selectedIds.length) {
    return "Choose a different plan for each mapped learning level.";
  }

  const plansById = new Map(plans.map((plan) => [plan.id, plan]));
  const missingIds = selectedIds.filter((planId) => !plansById.has(planId));
  if (missingIds.length) {
    return `Remove or replace unknown plan ids: ${missingIds.join(", ")}.`;
  }

  const inactivePlans = selectedIds
    .map((planId) => plansById.get(planId))
    .filter((plan): plan is Plan => Boolean(plan && plan.isActive === false));
  if (inactivePlans.length) {
    return `Activate or replace these plans before routing members to them: ${inactivePlans
      .map((plan) => plan.title || plan.id)
      .join(", ")}.`;
  }

  const nonLearningPlans = selectedIds
    .map((planId) => plansById.get(planId))
    .filter(
      (plan): plan is Plan =>
        Boolean(
          plan &&
            (plan.categoryId === "workout" || plan.planType === "exercise")
        )
    );
  if (nonLearningPlans.length) {
    return `Replace non-learning placement targets: ${nonLearningPlans
      .map((plan) => plan.title || plan.id)
      .join(", ")}.`;
  }

  return null;
};
